import React, { useEffect, useState } from 'react';

import type { VideoAssetComponent } from '../../../../../core/data-sources/content-components/types.js';

import { isObject } from '../../../../../core/utils/type-guards.js';

interface Dimension {
  width: number | string;
  height: number | string;
}

const normalizeVideo = (
  video: VideoAssetComponent,
  isMobile: boolean,
): Dimension => {
  const aspectRatio = parseFloat(video.videoAsset.metadata.aspectRatio);
  if (isMobile) {
    return {
      width: '100vw',
      height: `${100 / aspectRatio}vw`,
    };
  }
  const standardWidth =
    video?.presentationProperties?.addLayout === 'narrow' ? 490 : 784;
  const source = video.videoAsset.sourceFiles?.[0];

  if (!source) {
    return {
      width: standardWidth,
      height: standardWidth / aspectRatio,
    };
  }

  return {
    width: standardWidth,
    height: (standardWidth * source.height) / source.width,
  };
};

interface VideoAssetPlayerProps {
  video: VideoAssetComponent;
  isMobile: boolean;
}

const VideoAssetPlayer: React.FC<VideoAssetPlayerProps> = ({
  video,
  isMobile,
}) => {
  const id = parseInt(video.videoAsset.id);
  const [dimensions, setDimensions] = useState<Dimension | null>(null);

  // calculate dimensions as soon as video and isMobile props are available
  useEffect(() => {
    const newDimensions = normalizeVideo(video, isMobile);
    setDimensions(newDimensions);
  }, [video, isMobile]);

  useEffect(() => {
    const onReadyHandler = () => {
      if (!dimensions) {
        return;
      }

      new SVP.Player({
        id,
        vendor: video.provider.id,
        node: `video-${id}`,
        ...dimensions,
        recommended: false,
        pulse: {
          provider: 'aftenposten',
          decorator: (eventData: Record<string, unknown>) => {
            return {
              ...(isObject(eventData) ? eventData : {}),
              provider: {
                ...(isObject(eventData) && isObject(eventData.provider)
                  ? eventData.provider
                  : {}),
                productTag: 'vink',
                productType: 'ResponsiveWeb',
                product: 'fastenposten',
              },
            };
          },
        },
        repeat: video?.presentationProperties?.intention === 'looping',
        autoplay: 'viewable',
        autopause: 'viewable',
        mute: true,
      });
    };

    window.addEventListener('onSvpPlayerReady', onReadyHandler);

    return () => {
      window.removeEventListener('onSvpPlayerReady', onReadyHandler);
    };
  }, [id, video, dimensions]);

  return (
    <div className="svp-video-element">
      <div id={`video-${id}`}></div>
    </div>
  );
};

export default VideoAssetPlayer;
